import { createSelector } from 'reselect';
import {
  getCorrelationsWithAnswers,
  getCorrelationsByCategory,
  getDSACorrelations
} from '../dsa/correlationSelectors';

export const getSecurityOverviewAggregateData = createSelector(
  [getCorrelationsWithAnswers],
  (correlationsWithAnswers) => (
    correlationsWithAnswers.reduce((acc, correlation) => {
      const criticality = correlation.answer === 1 ? 'critical' : 'nonCritical';
      const adequacy = correlation.indexLevel;

      let group = acc[criticality];
      let overrides = {};
      group[adequacy] += 1;
      overrides[criticality] = group;

      return Object.assign({}, acc, overrides);
    }, {
      critical: {
        acceptable: 0,
        rudimentary: 0,
        inadequate: 0
      },
      nonCritical: {
        acceptable: 0,
        rudimentary: 0,
        inadequate: 0
      }
    })
  )
);

export const getSecurityOverviewItemizedData = createSelector(
  [getCorrelationsByCategory, getDSACorrelations],
  (categories, dsaCorrelations) => {
    const groups = [
      ['critical', 'acceptable'],
      ['critical', 'rudimentary'],
      ['critical', 'inadequate'],
      ['nonCritical', 'acceptable'],
      ['nonCritical', 'rudimentary'],
      ['nonCritical', 'inadequate']
    ];

    const defaultValue = {
      critical: {
        acceptable: [],
        rudimentary: [],
        inadequate: []
      },
      nonCritical: {
        acceptable: [],
        rudimentary: [],
        inadequate: []
      }
    };

    if (!dsaCorrelations) {
      return defaultValue;
    }

    return groups.reduce((acc, pair) => {
      const [criticality, adequacy] = pair;
      const critical = criticality === 'critical';

      const categoriesWithFilteredCorrelations = categories.map(category => {
        const answeredCorrelations = category.correlations.map(correlation => {
          const dsaCorrelation = dsaCorrelations.find(dsaCorrelation => dsaCorrelation.correlation === correlation.id);
          return Object.assign({}, correlation, dsaCorrelation);
        });

        const filteredCorrelations = answeredCorrelations.filter(correlation => (
          (critical ? correlation.answer === 1 : correlation.answer !== 1) &&
          correlation.indexLevel === adequacy
        ));

        return Object.assign({}, category, {
          correlations: filteredCorrelations
        });
      });

      let group = acc[criticality];
      let overrides = {};
      group[adequacy] = categoriesWithFilteredCorrelations;
      overrides[criticality] = group;
      return Object.assign({}, acc, overrides);

    }, defaultValue);
  }
);

const getSecurityDashboardCategories = state => state.dsaConfig && state.dsaConfig.dashboardCategories || [];
const getSecurityDashboardSubcategories = state => state.dsaConfig && state.dsaConfig.dashboardSubCategories || [];
const getSecurityDashboards = state => state.dsa && state.dsa.dashboards || [];

const getSecurityDashboardCategoriesWithSubcategories = createSelector(
  [getSecurityDashboardCategories, getSecurityDashboardSubcategories],
  (categories, subcategories) => {
    return categories.map(category => (
      Object.assign({}, category, {
        subcategories: subcategories.filter(subcategory => subcategory.category === category.id)
      })
    ));
  }
);

export const getSecurityDashboardsByCategory = createSelector(
  [getSecurityDashboards, getSecurityDashboardCategoriesWithSubcategories],
  (dashboards, categories) => {
    return categories.map(category => {
      const subcategoriesWithDashboards = category.subcategories.map(subcategory => {
        return Object.assign({}, subcategory, {
          dashboards: dashboards.filter(dashboard => dashboard.subcategory === subcategory.id)
        });
      });

      return Object.assign({}, category, {
        subcategories: subcategoriesWithDashboards
      });
    });
  }
);

export const getSecurityDashboardCategory = (state, categoryId) => {
  const categories = getSecurityDashboardsByCategory(state);
  return categories.find(category => category.id === categoryId);
};


export const getEnabledSecurityDashboardCount = createSelector(
  [getSecurityDashboards],
  (dashboards) => {
    return dashboards.filter(dashboard => dashboard.indexLevel === 'acceptable').length;
  }
);

export const getTotalSecurityDashboardCount = createSelector(
  [getSecurityDashboards],
  (dashboards) => {
    return dashboards.length;
  }
);


export const getSecurityDashboardsByCategoryInCategoriesWithSingleSubcategory = createSelector(
  [getSecurityDashboardsByCategory],
  (categories) => categories.filter(category => category.subcategories.length === 1)
);

export const getSecurityDashboardsByCategoryInCategoriesWithMultipleSubcategories = createSelector(
  [getSecurityDashboardsByCategory],
  (categories) => categories.filter(category => category.subcategories.length > 1)
);

export const getCriticalSecurityCorrelationsCount = createSelector(
  [getSecurityOverviewAggregateData],
  (aggregateData) => (
    aggregateData.critical.acceptable +
    aggregateData.critical.rudimentary +
    aggregateData.critical.inadequate
  )
);

export const getAcceptableCriticalSecurityCorrelationsCount = createSelector(
  [getSecurityOverviewAggregateData],
  (aggregateData) => aggregateData.critical.acceptable
);





// WEBPACK FOOTER //
// ./src/selectors/analytics/securitySelectors.js